import React, { useState } from 'react';
import { logger } from '../../utils/logger';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Checkbox,
  FormControlLabel,
  Link,
  Divider,
  Alert,
} from '@mui/material';
import {
  Gavel as GavelIcon,
  CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';

interface LegalAcceptanceModalProps {
  open: boolean;
  onAccept: () => Promise<void>;
  canClose?: boolean; // If false, user MUST accept to proceed
}

const LegalAcceptanceModal: React.FC<LegalAcceptanceModalProps> = ({
  open,
  onAccept,
  canClose = false,
}) => {
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [acceptedPrivacy, setAcceptedPrivacy] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleAccept = async () => {
    if (!acceptedTerms || !acceptedPrivacy) {
      setError('Morate prihvatiti i Uslove korišćenja i Politiku privatnosti.');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      await onAccept();
    } catch (err: any) {
      logger.error('Legal acceptance error:', err);
      setError('Greška pri prihvatanju. Pokušajte ponovo.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={canClose ? undefined : () => {}}
      maxWidth="md"
      fullWidth
      disableEscapeKeyDown={!canClose}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <GavelIcon sx={{ fontSize: 32, color: 'primary.main', mr: 2 }} />
          <Typography variant="h5" component="span">
            Prihvatanje Pravnih Uslova
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        <Alert severity="info" sx={{ mb: 3 }}>
          Pre nego što nastavite sa korišćenjem OmniPass platforme, potrebno je da
          pročitate i prihvatite naše pravne dokumente.
        </Alert>

        <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
          📋 Uslovi Korišćenja
        </Typography>
        <Typography variant="body2" color="text.secondary" paragraph>
          Uslovi korišćenja definišu pravila i odgovornosti za korišćenje OmniPass
          platforme, uključujući kreiranje i upravljanje digitalnim novčanicima (Apple
          Wallet, Google Wallet).
        </Typography>
        <Link
          href="/docs/terms-sr"
          target="_blank"
          rel="noopener noreferrer"
          sx={{ display: 'block', mb: 2 }}
        >
          📄 Pročitajte Uslove korišćenja (srpski)
        </Link>

        <FormControlLabel
          control={
            <Checkbox
              checked={acceptedTerms}
              onChange={(e) => setAcceptedTerms(e.target.checked)}
              color="primary"
            />
          }
          label={
            <Typography variant="body2">
              Pročitao/la sam i prihvatam <strong>Uslove korišćenja</strong>
            </Typography>
          }
        />

        <Divider sx={{ my: 3 }} />

        <Typography variant="h6" gutterBottom>
          🔒 Politika Privatnosti
        </Typography>
        <Typography variant="body2" color="text.secondary" paragraph>
          Politika privatnosti objašnjava kako prikupljamo, koristimo i štitimo vaše
          lične podatke u skladu sa GDPR i ZZPL propisima.
        </Typography>
        <Link
          href="/docs/privacy-sr"
          target="_blank"
          rel="noopener noreferrer"
          sx={{ display: 'block', mb: 2 }}
        >
          📄 Pročitajte Politiku privatnosti (srpski)
        </Link>

        <FormControlLabel
          control={
            <Checkbox
              checked={acceptedPrivacy}
              onChange={(e) => setAcceptedPrivacy(e.target.checked)}
              color="primary"
            />
          }
          label={
            <Typography variant="body2">
              Pročitao/la sam i prihvatam <strong>Politiku privatnosti</strong>
            </Typography>
          }
        />

        {error && (
          <Alert severity="error" sx={{ mt: 3 }}>
            {error}
          </Alert>
        )}

        <Box
          sx={{
            mt: 3,
            p: 2,
            bgcolor: 'grey.50',
            borderRadius: 2,
            border: '1px solid',
            borderColor: 'grey.200',
          }}
        >
          <Typography variant="caption" color="text.secondary">
            <strong>Napomena:</strong> Prihvatanjem ovih dokumenata potvrđujete da ste
            ih pročitali i razumeli. Vaš prihvat će biti zabeležen sa datumom i vremenom.
          </Typography>
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <Button
          onClick={handleAccept}
          variant="contained"
          color="primary"
          disabled={!acceptedTerms || !acceptedPrivacy || loading}
          startIcon={loading ? undefined : <CheckCircleIcon />}
        >
          {loading ? 'Obrađujem...' : 'Prihvatam Uslove'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default LegalAcceptanceModal;
